import { Info } from "lucide-react";

interface ArticleBlocksProps {
  content: string;
}

/**
 * Shared Figma article body renderer (dc.html:4049): headings, paragraphs,
 * numbered badge steps, bullets and tip callouts. Used by the reader modal
 * and the editor Preview tab so both render identically.
 */
export function ArticleBlocks({ content }: ArticleBlocksProps) {
  const paragraphs = (content || "").split("\n\n");
  if (paragraphs.every((p) => !p.trim())) {
    return <p className="text-body-sm text-[var(--color-text-tertiary)]">Nothing to preview yet.</p>;
  }
  return (
    <>
      {paragraphs.map((paragraph, index) => {
        const trimmed = paragraph.trim();
        // Tip / quote callout
        if (trimmed.startsWith(">")) {
          return (
            <div
              key={index}
              className="flex items-start gap-[11px] p-[13px_15px] rounded-[12px] bg-[var(--color-info-light)] border border-[color-mix(in_srgb,var(--color-info)_35%,transparent)] my-[6px] mb-[14px]"
            >
              <Info className="w-[18px] h-[18px] text-[var(--color-info)] flex-none mt-[1px]" />
              <span className="text-body-sm leading-[1.5] text-[var(--color-secondary-800)]">
                {trimmed.replace(/^>\s?/, "")}
              </span>
            </div>
          );
        }
        // Markdown heading
        if (/^#{1,3}\s/.test(trimmed)) {
          return (
            <h4
              key={index}
              className="mt-[18px] mb-2 text-heading-sm text-[var(--color-text-primary)]"
            >
              {trimmed.replace(/^#{1,3}\s/, "")}
            </h4>
          );
        }
        // Bullet list
        if (/^([\-•*]\s)/m.test(trimmed)) {
          const items = trimmed.split("\n").filter((line) => line.trim());
          return (
            <div key={index} className="mb-3">
              {items.map((item, i) => (
                <div key={i} className="flex gap-[11px] mb-2.5">
                  <span className="w-1.5 h-1.5 rounded-full flex-none bg-[var(--color-primary-400)] mt-[9px] mx-[9px]" />
                  <span className="text-body-sm leading-[1.6] text-[var(--color-text-strong)] pt-0.5">
                    {item.replace(/^([\-•*]\s)/, "")}
                  </span>
                </div>
              ))}
            </div>
          );
        }
        // Numbered list
        if (/^\d+\./.test(trimmed)) {
          const items = trimmed.split("\n").filter((line) => line.trim());
          return (
            <div key={index} className="mb-3">
              {items.map((item, i) => {
                const mark = item.match(/^(\d+)\./)?.[1] || `${i + 1}`;
                return (
                  <div key={i} className="flex gap-[11px] mb-2.5">
                    <span className="w-6 h-6 rounded-[7px] flex-none grid place-items-center bg-[var(--color-primary-100)] text-[var(--color-primary-700)] text-body-xs font-extrabold">
                      {mark}
                    </span>
                    <span className="text-body-sm leading-[1.6] text-[var(--color-text-strong)] pt-0.5">
                      {item.replace(/^\d+\.\s*/, "")}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        }
        // Bold-lead block (**Title**: rest)
        if (trimmed.startsWith("**") && trimmed.includes("**:")) {
          const [title, ...rest] = trimmed.split("**:");
          return (
            <div key={index} className="mb-6 last:mb-0">
              <h4 className="font-extrabold text-heading-sm text-[var(--color-text-primary)] mb-2">
                {title.replace(/\*\*/g, "")}
              </h4>
              <p className="text-[var(--color-text-strong)] text-body-md leading-[1.7]">
                {rest.join("").trim()}
              </p>
            </div>
          );
        }
        return (
          <p key={index} className="mb-3 text-body-md leading-[1.7] text-[var(--color-text-strong)]">
            {paragraph}
          </p>
        );
      })}
    </>
  );
}

export default ArticleBlocks;
